import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

interface MenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const Menu: React.FC<MenuProps> = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const isAdmin = user?.role === 'super_admin' || user?.role === 'org_admin' || user?.role === 'team_admin';

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000,
        }}
        onClick={onClose}
      />

      {/* Menu */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '280px',
          height: '100vh',
          backgroundColor: theme.colors.surface,
          borderRight: `1px solid ${theme.colors.border}`,
          zIndex: 1001,
          transform: isOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.3s ease-in-out',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: theme.fonts.primary,
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px',
            borderBottom: `1px solid ${theme.colors.border}`,
            backgroundColor: theme.colors.primary,
            color: theme.colors.primaryText,
          }}
        >
          <h2 style={{
            margin: 0,
            fontSize: theme.typography.h3.fontSize,
            fontWeight: theme.typography.h3.fontWeight,
            fontFamily: theme.fonts.display,
          }}>
            Menu
          </h2>
          {user && (
            <p style={{
              margin: '8px 0 0 0',
              fontSize: theme.typography.body.fontSize,
              opacity: 0.9,
            }}>
              {user.displayName} {getRoleEmoji(user.role)}
            </p>
          )}
        </div>

        {/* Menu Items */}
        <nav style={{ flex: 1, padding: '16px 0' }}>
          <ul style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
          }}>
            <MenuItem
              label="Dashboard"
              icon="🏠"
              onClick={() => {
                navigate('/dashboard');
                onClose();
              }}
              theme={theme}
            />
            {isAdmin && (
              <>
                <MenuItem
                  label="Challenges"
                  icon="🎯"
                  onClick={() => {
                    navigate('/challenge');
                    onClose();
                  }}
                  theme={theme}
                />
                <MenuItem
                  label="Leaderboard"
                  icon="🏆"
                  onClick={() => {
                    navigate('/leaderboard');
                    onClose();
                  }}
                  theme={theme}
                />
              </>
            )}
            <MenuItem
              label="Settings"
              icon="⚙️"
              onClick={() => {
                navigate('/user-settings');
                onClose();
              }}
              theme={theme}
            />
            {isAdmin && (
              <MenuItem
                label="Admin Panel"
                icon="👑"
                onClick={() => {
                  navigate('/admin');
                  onClose();
                }}
                theme={theme}
                highlight={true}
              />
            )}
          </ul>
        </nav>

        {/* Footer */}
        <div
          style={{
            padding: '16px 20px',
            borderTop: `1px solid ${theme.colors.border}`,
          }}
        >
          <button
            onClick={handleLogout}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: theme.colors.error,
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: theme.typography.body.fontSize,
              fontFamily: theme.fonts.primary,
              cursor: 'pointer',
              transition: 'opacity 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.8';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
          >
            Logout
          </button>
        </div>
      </div>
    </>
  );
};

interface MenuItemProps {
  label: string;
  icon: string;
  onClick: () => void;
  theme: any;
  highlight?: boolean;
}

const MenuItem: React.FC<MenuItemProps> = ({ label, icon, onClick, theme, highlight = false }) => {
  return (
    <li>
      <button
        onClick={onClick}
        style={{
          width: '100%',
          padding: '16px 20px',
          backgroundColor: 'transparent',
          border: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          fontSize: theme.typography.body.fontSize,
          fontFamily: theme.fonts.primary,
          color: highlight ? theme.colors.primary : theme.colors.text,
          fontWeight: highlight ? 'bold' : 'normal',
          cursor: 'pointer',
          transition: 'background-color 0.2s ease',
          textAlign: 'left',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = theme.colors.hover;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <span style={{ fontSize: '20px' }}>{icon}</span>
        <span>{label}</span>
      </button>
    </li>
  );
};

// Helper function to get role emoji
const getRoleEmoji = (role?: string) => {
  switch (role) {
    case 'super_admin':
      return '👑';
    case 'org_admin':
      return '⚡';
    case 'team_lead':
      return '🎯';
    case 'member':
    default:
      return '👤';
  }
};

export default Menu; 